import { Injectable } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { from } from 'rxjs';
import { take } from 'rxjs/operators';
import sweetalert2, { SweetAlertOptions } from 'sweetalert2';
import { RepoAuthService } from '../../../data/repositories/repo-auth.service';
import { AccountIntegrationsStoresService } from '../../../pages/account';
import { IntegrationConnectionTypesByNumber } from '../../../pages/integrations-v2';
import { IntegrationSupplierStores } from '../../../pages/integrations-v2/integration-supplier/domain/apis/integration-supplier.stores';
import { CatalogueEventsService, CatalogueImportProgressEventArgs } from '../events/catalogue-events.service';
import { EstimateEventsService, EstimateLivePricingResponseEventArgs } from '../events/estimate-events.service';
import { NotificationService } from '../notification.service';
import { FeatureService } from '../permission';
import { StoresService } from '../stores';

@Injectable({
    providedIn: 'root',
})
export class UserEventService {
    triggerRefreshUserEventFunc = this.triggerRefreshUserEvent.bind(this);
    triggerDeleteUserEventFunc = this.triggerDeleteUserEvent.bind(this);
    triggerUserVerifiedEventFunc = this.triggerUserVerifiedEvent.bind(this);
    triggerPlanChangedEventFunc = this.triggerPlanChangedEvent.bind(this);
    triggerAddOnsChangedEventFunc = this.triggerAddOnsChangedEvent.bind(this);
    // @ts-ignore TS7006
    triggerLoadIntegrationConnectionEventFunc = ((event) => this.triggerLoadIntegrationConnectionEvent(event)).bind(this);
    triggerUserPasswordChangedEventFunc = this.triggerUserPasswordChangedEvent.bind(this);
    triggerUserPasswordRemovedEventFunc = this.triggerUserPasswordRemovedEvent.bind(this);
    triggerNoticeChangedEventFunc = this.triggerNoticeChangedEvent.bind(this);
    // @ts-ignore TS7006
    triggerQuoteRequestEventFunc = ((recipient) => this.triggerQuoteRequestEvent(recipient)).bind(this);
    // @ts-ignore TS7006
    triggerRefreshIntegrationStatusFunc = ((status) => this.triggerRefreshIntegrationStatusEvent(status)).bind(this);
    triggerPlanConverterJobEventFunc = ((status: any) => this.triggerPlanConverterJobFinishedEvent(status)).bind(this);
    triggerLivePricingResponseEventFunc = (e: any) => this.triggerLivePricingResponseEvent(e);
    triggerImportProgressUpdatedEventFunc = this.triggerImportProgressUpdatedEvent.bind(this);

    constructor(
        private readonly authService: RepoAuthService,
        private readonly storesService: StoresService,
        private readonly integrationSupplierStores: IntegrationSupplierStores,
        private readonly accountIntegrationsStoresService: AccountIntegrationsStoresService,
        private readonly notificationService: NotificationService,
        private readonly estimateEvents: EstimateEventsService,
        private readonly catalogueEvents: CatalogueEventsService,
        private readonly featureService: FeatureService
    ) {}

    public watchEventStatusChanges() {
        this.storesService.eventStatus$.subscribe((status: signalR.HubConnectionState) => {
            if (status === signalR.HubConnectionState.Connected) {
                this.deregisterEvents();
                this.registerEvents();
            } else {
                this.deregisterEvents();
            }
        });
    }

    private registerEvents() {
        // TODO:: LD Not available at this point...
        const events = this.notificationService.connection;

        if (events.state !== signalR.HubConnectionState.Connected) {
            return;
        }

        events.on('RefreshUserTokenRequired', this.triggerRefreshUserEventFunc);
        events.on('UserAccountRemoved', this.triggerDeleteUserEventFunc);
        events.on('UserAccountVerified', this.triggerUserVerifiedEventFunc);
        events.on('TenantPlanChanged', this.triggerPlanChangedEventFunc);
        events.on('TenantAddOnsChanged', this.triggerAddOnsChangedEventFunc);
        events.on('IncomingConnectionsCountUpdated', this.triggerLoadIntegrationConnectionEventFunc);
        events.on('UserPasswordChanged', this.triggerUserPasswordChangedEventFunc);
        events.on('UserPasswordRemoved', this.triggerUserPasswordRemovedEventFunc);
        events.on('NoticeCreated', this.triggerNoticeChangedEventFunc);
        events.on('NoticeRead', this.triggerNoticeChangedEventFunc);
        events.on('QuoteRequestSubmitted', this.triggerQuoteRequestEventFunc);
        events.on('QuoteRequestResponded', this.triggerQuoteRequestEventFunc);
        events.on('QuoteRequestCompleted', this.triggerQuoteRequestEventFunc);
        events.on('IntegrationStatusUpdated', this.triggerRefreshIntegrationStatusFunc);
        events.on('PlanConverterJobFinished', this.triggerPlanConverterJobEventFunc);
        events.on('LivePricingResponse', this.triggerLivePricingResponseEventFunc);
        events.on('ImportProgressUpdated', this.triggerImportProgressUpdatedEventFunc);
    }

    private deregisterEvents() {
        // TODO:: LD Not available at this point...
        const events = this.notificationService.connection;

        if (events) {
            events.off('RefreshUserTokenRequired', this.triggerRefreshUserEventFunc);
            events.off('UserAccountRemoved', this.triggerDeleteUserEventFunc);
            events.off('UserAccountVerified', this.triggerUserVerifiedEventFunc);
            events.off('TenantPlanChanged', this.triggerPlanChangedEventFunc);
            events.off('IncomingConnectionsCountUpdated', this.triggerLoadIntegrationConnectionEventFunc);
            events.off('UserPasswordChanged', this.triggerUserPasswordChangedEventFunc);
            events.off('NoticeCreated', this.triggerNoticeChangedEventFunc);
            events.off('NoticeRead', this.triggerNoticeChangedEventFunc);
            events.off('QuoteRequestSubmitted', this.triggerQuoteRequestEventFunc);
            events.off('QuoteRequestResponded', this.triggerQuoteRequestEventFunc);
            events.off('QuoteRequestCompleted', this.triggerQuoteRequestEventFunc);
            events.off('IntegrationStatusUpdated', this.triggerRefreshIntegrationStatusFunc);
            events.off('PlanConverterJobFinished', this.triggerPlanConverterJobEventFunc);
            events.off('LivePricingResponse', this.triggerLivePricingResponseEventFunc);
            events.off('ImportProgressUpdated', this.triggerImportProgressUpdatedEventFunc);
        }
    }

    private triggerRefreshUserEvent() {
        from(this.authService.forceTokenRefresh())
            .pipe(take(1))
            .subscribe(() => {
                sweetalert2
                    .fire({
                        title: 'Your Permissions Have Changed',
                        text: 'Your permissions were changed by the account admin. Please refresh your browser to continue.',
                        icon: 'error',
                        customClass: { popup: 'bx-dialog bx-dialog-error' },
                        allowOutsideClick: false,
                        showConfirmButton: true,
                        confirmButtonText: 'Refresh',
                        reverseButtons: true,
                        showCancelButton: false,
                        animation: false,
                    })
                    .then(() => {
                        window.location.href = '/dashboard';
                    });
            });
    }

    private triggerDeleteUserEvent() {
        this.authService.clearAllStorage();

        // @ts-ignore TS2769
        sweetalert2
            .fire({
                title: 'Your Account Have Been Deleted',
                text: 'Please contact your account admin for any issues.',
                icon: 'error',
                customClass: { popup: 'bx-dialog bx-dialog-error' },
                allowOutsideClick: false,
                showConfirmButton: true,
                confirmButtonText: 'Logout',
                reverseButtons: true,
                showCancelButton: false,
                animation: false,
            })
            .then(() => {
                this.authService.logout();
            });
    }

    private triggerUserPasswordChangedEvent() {
        this.authService.clearAllStorage();

        // @ts-ignore TS2769
        sweetalert2
            .fire({
                title: 'You changed your password for this account',
                text: 'This account uses an old password that you recently changed. Please log out to continue. Contact your account admin if you have any issues.',
                type: 'error',
                customClass: { popup: 'bx-dialog bx-dialog-error' },
                allowOutsideClick: false,
                showConfirmButton: true,
                confirmButtonText: 'Logout',
                reverseButtons: true,
                showCancelButton: false,
                animation: false,
            } as SweetAlertOptions)
            .then(() => {
                this.authService.logout();
            });
    }

    private triggerUserPasswordRemovedEvent() {
        this.authService.clearAllStorage();

        // @ts-ignore TS2769
        sweetalert2
            .fire({
                title: 'You removed your password for this account',
                text: 'You can now only log in using a code sent to your email address. Please log out to continue. Contact your account admin if you have any issues.',
                type: 'error',
                customClass: { popup: 'bx-dialog bx-dialog-error' },
                allowOutsideClick: false,
                showConfirmButton: true,
                confirmButtonText: 'Logout',
                reverseButtons: true,
                showCancelButton: false,
                animation: false,
            } as SweetAlertOptions)
            .then(() => {
                this.authService.logout();
            });
    }

    private triggerPlanChangedEvent() {
        this.authService.forceTokenRefresh();
        this.featureService.rebuildFeaturesCache();
    }

    private triggerAddOnsChangedEvent() {
        this.featureService.rebuildFeaturesCache();
    }

    private triggerUserVerifiedEvent() {
        // @ts-ignore TS2769
        sweetalert2.fire({
            title: 'Thank you for validating your email address.',
            type: 'success',
            confirmButtonText: 'Continue',
            customClass: { popup: 'bx-dialog bx-dialog-success' },
            allowOutsideClick: true,
            animation: false,
            timer: null,
        });
    }

    // @ts-ignore TS7006
    private triggerLoadIntegrationConnectionEvent(event) {
        if (event?.ConnectionType === IntegrationConnectionTypesByNumber.SupplierInvitesClient) {
            this.integrationSupplierStores.refreshIntegrationConnections();
        }
    }

    private triggerNoticeChangedEvent() {
        this.storesService.refreshNotice();
    }

    // @ts-ignore TS7006
    private triggerQuoteRequestEvent(recipient) {
        this.storesService.refreshQuoteRequestEvent = recipient.ID_QuoteRequest;
    }

    // @ts-ignore TS7006
    private triggerRefreshIntegrationStatusEvent(status) {
        this.accountIntegrationsStoresService.refreshAccountSyncStatus(status);
    }

    private triggerPlanConverterJobFinishedEvent(event: any) {
        this.estimateEvents.planConverterJobFinished(event);
    }

    private triggerLivePricingResponseEvent(event: EstimateLivePricingResponseEventArgs | null) {
        this.estimateEvents.livePricingResponse(event);
    }

    private triggerImportProgressUpdatedEvent(event: CatalogueImportProgressEventArgs | null) {
        this.catalogueEvents.importProgressUpdated(event);
    }
}
